import type { StorageAdapter } from '@/services/storage/storage-adapter';
import { recordPendingDelete } from './sync-service';

/**
 * Hunt service layer (ADR-019 soft-delete propagation).
 *
 * Mirrors PropertyService: the store calls the service, the service owns the
 * pending-delete enqueue (RULE-081 — services never import stores; the
 * store → service → adapter+enqueue direction stays clean).
 *
 * Scope: entity DELETES only (deleteHunt, deleteSit). The store still performs
 * its other hunt operations (load/create/update) directly against the adapter;
 * this service exists solely to route deletes through recordPendingDelete so
 * the remote row's deleted_at gets set on the next sync push (preventing
 * cross-device resurrection).
 *
 * Child-cascade decision (OPTION (a) — single 'hunt' enqueue on deleteHunt, no
 * cascading child enqueues). NOTE (#1316): the original rationale for this —
 * that harvestRecord had no deleted_at column and softDeleteHarvestRecord was a
 * HARD delete — is now FALSIFIED. Migration 0022 added harvest_records.deleted_at
 * and supabase-repository.softDeleteHarvestRecord is a true soft delete, as is
 * softDeleteHuntSit. The decision itself is left UNCHANGED here regardless:
 * whether deleting a hunt should cascade soft-deletes to its sits and harvests
 * is a distinct question from whether an explicit sit/harvest delete enqueues
 * one, and #1316 is scoped to the latter. Cascade handling stays a follow-up.
 */
export class HuntService {
  constructor(private adapter: StorageAdapter) {}

  async deleteHunt(id: string): Promise<void> {
    await this.adapter.deleteHunt(id);
    await recordPendingDelete(id, 'hunt');
  }

  /**
   * Delete a hunt sit (#1316). Exact mirror of deleteHunt: the adapter's hard
   * local delete first, then the pending-delete enqueue — so a failed local
   * delete never leaves a phantom soft-delete queued against a still-live row.
   */
  async deleteSit(id: string): Promise<void> {
    await this.adapter.deleteHuntSit(id);
    await recordPendingDelete(id, 'huntSit');
  }
}
